import React, { useEffect, useState } from "react";
import { usePlans } from "./plans";
import { api } from "@/lib/api";
import { GstRows } from "@/lib/gst";
import { Sparkles, Zap, Crown, Gem, BadgePercent, Check, ChevronDown } from "lucide-react";

/**
 * Plan pricing helpers.
 *
 * IMPORTANT: no price or discount is calculated in the browser. Every number comes from the backend
 * (`plan.pricing` on /content and /pricing/plans, or a quote from /pricing/quote and
 * /subscription/quote). These helpers only format and lay out what the server returned.
 */
export const inr = (n) => {
  const v = Number(n || 0);
  const frac = Math.abs(v - Math.round(v)) > 0.004;   // whole rupees stay clean (₹3,499), fractions always show 2 digits (₹9,447.30)
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: frac ? 2 : 0, maximumFractionDigits: 2 })}`;
};

export function readPricing(plan) {
  const pr = plan?.pricing;
  if (!pr || !pr.valid) return null;
  return {
    mrp: Number(pr.mrp),
    selling: Number(pr.selling_price),
    discount: Number(pr.discount_amount),
    pct: Number(pr.discount_percent),
    hasDiscount: !!pr.has_discount,
  };
}

const fmtPct = (p) => (Number.isInteger(p) ? String(p) : p.toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));

/* ------------------------------------------------------------------------------------------------
 * Creative plan tags. The ADMIN chooses the tag text, colour and popup highlight chips for every plan
 * (Admin -> Plan pricing -> "Plan tags & popup highlights"). Defaults below apply until admin saves.
 * --------------------------------------------------------------------------------------------- */
export const TAG_COLORS = {
  sky:     "bg-sky-500/12 text-sky-600 dark:text-sky-300 ring-sky-500/30",
  amber:   "bg-amber-500/15 text-amber-600 dark:text-amber-300 ring-amber-500/35",
  violet:  "bg-violet-500/12 text-violet-600 dark:text-violet-300 ring-violet-500/30",
  gold:    "bg-gradient-to-r from-yellow-400/25 to-orange-400/25 text-orange-600 dark:text-yellow-300 ring-yellow-500/40",
  emerald: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-300 ring-emerald-500/30",
  rose:    "bg-sky-500/12 text-sky-600 dark:text-sky-300 ring-sky-500/30",   // legacy value, now shown in the brand blue
  pink:    "bg-sky-500/12 text-sky-600 dark:text-sky-300 ring-sky-500/30",   // legacy value, now shown in the brand blue
  slate:   "bg-slate-500/12 text-slate-600 dark:text-slate-300 ring-slate-500/30",
};
/** Colours an admin can pick (legacy "rose" / "pink" values still render, in brand blue). */
export const TAG_COLOR_CHOICES = Object.keys(TAG_COLORS).filter((k) => k !== "rose" && k !== "pink");
export const DEFAULT_TAGS = {
  essential: { label: "Fresh Start", color: "sky", Icon: Sparkles },
  standard:  { label: "Smart Saver", color: "amber", Icon: Zap },
  premium:   { label: "Premium Glow", color: "violet", Icon: Gem },
  vip:       { label: "VIP Elite", color: "gold", Icon: Crown },
};
const TAG_FALLBACK = [DEFAULT_TAGS.essential, DEFAULT_TAGS.standard, DEFAULT_TAGS.premium, DEFAULT_TAGS.vip];

/** Returns null when the admin switched the tag off for this plan. */
export function planTag(plan, index = 0) {
  if (plan?.tag_enabled === false) return null;
  const base = DEFAULT_TAGS[plan?.id] || TAG_FALLBACK[index % TAG_FALLBACK.length];
  const label = (plan?.tag && String(plan.tag).trim()) || base.label;
  const color = TAG_COLORS[plan?.tag_color] ? plan.tag_color : base.color;
  return { label, color, cls: TAG_COLORS[color], Icon: base.Icon };
}

export function PlanTag({ plan, index = 0, className = "" }) {
  const t = planTag(plan, index);
  if (!t) return null;
  const Icon = t.Icon;
  return (
    <span data-testid={`plan-tag-${plan?.id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.6875rem] font-bold uppercase tracking-[.08em] ring-1 ${t.cls} ${className}`}>
      <Icon className="h-3 w-3" />{t.label}
    </span>
  );
}

/** MRP (small, struck through) + selling price (large) + savings. Shows no discount UI when none applies. */
export function PlanPrice({ plan, interval, size = "lg", className = "" }) {
  const pr = readPricing(plan);
  if (!pr) return <div className={`text-sm text-muted-foreground ${className}`}>Price unavailable</div>;
  const big = size === "xl" ? "text-5xl" : size === "md" ? "text-3xl" : "text-[2.25rem]";
  return (
    <div className={className} data-testid={`plan-price-${plan.id}`}>
      {pr.hasDiscount && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">MRP</span>
          <span className="text-base font-semibold text-red-500/80 line-through decoration-2" data-testid="plan-mrp">{inr(pr.mrp)}</span>
          <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[0.6875rem] font-bold text-white shadow-sm shadow-emerald-500/30">
            Save {inr(pr.discount)} · {fmtPct(pr.pct)}% OFF
          </span>
        </div>
      )}
      <div className="mt-1 flex items-baseline gap-1">
        <span className={`${big} font-bold tracking-tight ${pr.hasDiscount ? "bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent" : ""}`} data-testid="plan-selling-price">{inr(pr.selling)}</span>
        {interval && <span className="text-sm text-muted-foreground">/ {interval}</span>}
      </div>
      {pr.hasDiscount && <div className="text-[0.6875rem] font-semibold uppercase tracking-[.12em] text-emerald-600 dark:text-emerald-400">Your NAVI price</div>}
    </div>
  );
}

/** Server quote -> Original MRP / Plan Discount / (Current plan credit) / (Promo) / Total Payable. */
export function PriceBreakdown({ quote, promoDiscount = 0, promoCode = "", className = "", gst = null }) {
  if (!quote) return null;
  const payable = Math.max(0, Number(quote.subtotal) - Number(promoDiscount || 0));
  const row = "flex items-center justify-between gap-3 py-1.5 text-sm";
  return (
    <div className={`rounded-2xl border border-border bg-card p-4 ${className}`} data-testid="price-breakdown">
      <div className={row}><span className="text-muted-foreground">{quote.plan_name} Plan MRP{quote.months > 1 ? ` × ${quote.months} months` : ""}</span><span>{inr(quote.mrp)}</span></div>
      <div className={row}>
        <span className="text-muted-foreground">Plan Discount</span>
        <span className={quote.plan_discount > 0 ? "text-emerald-600 dark:text-emerald-400 font-medium" : ""}>
          {quote.plan_discount > 0 ? `-${inr(quote.plan_discount)}` : inr(0)}
        </span>
      </div>
      {quote.term_discount > 0 && (
        <div className={row} data-testid="term-discount-row">
          <span className="text-muted-foreground">{quote.months}-month extra discount ({fmtPct(Number(quote.term_discount_percent))}%)</span>
          <span className="text-emerald-600 dark:text-emerald-400 font-medium">-{inr(quote.term_discount)}</span>
        </div>
      )}
      {quote.plan_credit > 0 && (
        <div className={row}><span className="text-muted-foreground">Current plan credit</span><span className="text-emerald-600 dark:text-emerald-400 font-medium">-{inr(quote.plan_credit)}</span></div>
      )}
      {promoDiscount > 0 && (
        <div className={row}><span className="text-muted-foreground">Promo {promoCode}</span><span className="text-emerald-600 dark:text-emerald-400 font-medium">-{inr(promoDiscount)}</span></div>
      )}
      {gst ? (
        <GstRows bill={gst.bill} loading={gst.loading} failed={gst.failed} />
      ) : (
        <div className="mt-2 border-t border-border pt-3 flex items-center justify-between gap-3">
          <span className="font-semibold">Total Payable</span>
          <span className="text-2xl font-semibold tracking-tight" data-testid="amount-payable">{inr(payable)}</span>
        </div>
      )}
    </div>
  );
}

/** Same layout from a stored payment intent (price snapshot taken when the order was created). */
export function intentToQuote(intent) {
  if (!intent) return null;
  return {
    plan_name: intent.plan_name,
    mrp: intent.mrp ?? intent.subtotal,
    plan_discount: intent.plan_discount ?? 0,
    plan_credit: intent.plan_credit ?? 0,
    months: intent.months ?? 1,
    term_discount: intent.term_discount ?? 0,
    term_discount_percent: intent.term_discount_percent ?? 0,
    subtotal: intent.subtotal ?? intent.amount,
  };
}

/** Fetch a server-side quote. `subscriptionId`/`mode` are used by the logged-in upgrade flow. */
export async function fetchQuote(planId, { authenticated = false, mode = "new", subscriptionId = null, months = 1 } = {}) {
  const params = { plan_id: planId, months, _: Date.now() };
  if (authenticated) {
    params.mode = mode;
    if (subscriptionId) params.subscription_id = subscriptionId;
    return (await api.get("/subscription/quote", { params })).data;
  }
  return (await api.get("/pricing/quote", { params })).data;
}

export const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join(", ") : "") || e?.message || fallback;
};

/* ------------------------------------------------------------------------------------------------
 * Multi-month terms (3 / 6 / 12). Percentages and totals come from the server (/pricing/plans ->
 * plan.terms); the admin changes the percentages in Admin -> Plan pricing.
 * --------------------------------------------------------------------------------------------- */
export function usePlanTerms() {
  const { plans } = usePlans();
  const m = {};
  (plans || []).forEach((p) => { m[p.id] = Array.isArray(p.terms) ? p.terms : []; });
  return m;
}

/** Highest extra term discount offered for a plan, e.g. 22 -> "Save up to 22% on 12 months". */
export const bestTerm = (options) =>
  (options || []).filter((o) => o.months > 1 && o.discount_percent > 0).sort((a, b) => b.discount_percent - a.discount_percent)[0] || null;

/**
 * Duration chooser. Default = ONE ROW of tiles (1 / 3 / 6 / 12 months); every tile stacks three short lines in
 * the same box: the duration, the total, and "per month · save %". Pass `list` for the old stacked rows.
 */
export function TermPicker({ options, value, onChange, interval = "month", compact = false, list = false }) {
  if (!options || options.length < 2) return null;
  if (list) return <TermList options={options} value={value} onChange={onChange} interval={interval} compact={compact} />;
  return (
    <div className="grid grid-cols-4 max-[400px]:grid-cols-2 gap-2" role="radiogroup" aria-label="Plan duration" data-testid="term-picker">
      {options.map((o) => {
        const active = o.months === value;
        const label = o.months === 1 ? `1 ${interval}` : `${o.months} ${interval}s`;
        const off = o.discount_percent > 0 ? `${fmtPct(o.discount_percent)}% off` : "";
        return (
          <button
            key={o.months}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`term-option-${o.months}`}
            onClick={() => onChange(o.months)}
            className={`group relative flex min-w-0 flex-col items-center justify-center rounded-2xl border px-1.5 text-center transition-all duration-200 ${compact ? "py-2.5" : "py-3.5"} ${active
              ? "border-primary bg-gradient-to-b from-primary/[0.12] to-primary/[0.03] shadow-[0_8px_24px_-12px_hsl(var(--primary)/.6)] ring-1 ring-primary/50"
              : "border-border/70 bg-card/70 hover:border-primary/40 hover:bg-card"}`}
          >
            {o.months === 12 && <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 px-2 py-px text-[0.5625rem] font-bold uppercase tracking-wider text-white shadow">Best value</span>}
            <span className={`text-[0.6875rem] font-semibold uppercase tracking-wide ${active ? "text-primary" : "text-muted-foreground"}`}>{label}</span>
            <span className="mt-0.5 text-[0.9375rem] font-bold leading-tight tracking-tight tabular-nums" data-testid={`term-final-${o.months}`}>{inr(o.final_total)}</span>
            <span className="mt-0.5 text-[0.625rem] leading-tight text-muted-foreground tabular-nums">
              {o.months > 1 ? `${inr(o.per_month)}/mo` : "billed monthly"}
            </span>
            <span className={`mt-1 min-h-[1.125rem] rounded-full px-1.5 text-[0.625rem] font-bold leading-[1.125rem] ${off ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" : "invisible"}`}>{off || "–"}</span>
          </button>
        );
      })}
    </div>
  );
}

function TermList({ options, value, onChange, interval, compact }) {
  return (
    <div className="space-y-2" role="radiogroup" aria-label="Plan duration" data-testid="term-picker">
      {options.map((o) => {
        const active = o.months === value;
        const label = o.months === 1 ? `1 ${interval}` : `${o.months} ${interval}s`;
        return (
          <button key={o.months} type="button" role="radio" aria-checked={active} data-testid={`term-option-${o.months}`} onClick={() => onChange(o.months)}
            className={`w-full text-left rounded-2xl border transition-all ${compact ? "px-3 py-2.5" : "px-4 py-3"} ${active ? "border-primary bg-primary/[0.06] ring-1 ring-primary/40" : "border-border bg-card hover:bg-accent/60"}`}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold">{label}</span>
              <span className="text-base font-semibold tracking-tight" data-testid={`term-final-${o.months}`}>{inr(o.final_total)}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** Highlight chips (shown only inside the expanded "details" area, never in the compact view). */
export function PopupHighlights({ plan, options }) {
  const pr = readPricing(plan);
  const best = bestTerm(options);
  const chips = [];
  if (pr?.hasDiscount) chips.push({ k: "save", Icon: BadgePercent, text: `Save ${inr(pr.discount)} / ${plan.interval || "month"}`, cls: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-300 ring-emerald-500/30" });
  if (best) chips.push({ k: "best", Icon: Zap, text: `Extra ${fmtPct(best.discount_percent)}% off on ${best.months} months`, cls: "bg-amber-500/15 text-amber-600 dark:text-amber-300 ring-amber-500/35" });
  const custom = Array.isArray(plan?.highlights)
    ? plan.highlights.filter((h) => h && h.text)
    : [{ text: "Doorstep service", color: "sky" }, { text: "Premium care", color: "violet" }];
  custom.forEach((h, i) => chips.push({ k: `c${i}`, Icon: Sparkles, text: h.text, cls: TAG_COLORS[h.color] || TAG_COLORS.sky }));
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="popup-highlights">
      {chips.map(({ k, Icon, text, cls }) => (
        <span key={k} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold ring-1 ${cls}`}><Icon className="h-3 w-3" />{text}</span>
      ))}
    </div>
  );
}

/** MRP + sale price on ONE compact line (used by the hover popup). */
function PopupPrice({ plan }) {
  const pr = readPricing(plan);
  if (!pr) return <div className="text-sm text-muted-foreground">Price unavailable</div>;
  return (
    <div className="flex flex-wrap items-end gap-x-3 gap-y-1" data-testid={`plan-price-${plan.id}`}>
      <div className="flex items-baseline gap-1">
        <span className={`text-[2rem] leading-none font-bold tracking-tight ${pr.hasDiscount ? "bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent" : ""}`} data-testid="plan-selling-price">{inr(pr.selling)}</span>
        {plan.interval && <span className="text-sm text-muted-foreground">/ {plan.interval}</span>}
      </div>
      {pr.hasDiscount && (
        <div className="flex items-center gap-2 pb-0.5">
          <span className="text-sm text-red-500/80 line-through decoration-2" data-testid="plan-mrp">{inr(pr.mrp)}</span>
          <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[0.6875rem] font-bold text-white shadow-sm shadow-emerald-500/30">{fmtPct(pr.pct)}% OFF</span>
        </div>
      )}
    </div>
  );
}

/**
 * Hover popup: when the cursor rests on a plan card, the plan opens over a blurred backdrop.
 * Fixed size, wider than tall, NO scrolling: price + one-row durations + CTA are always visible; the plan
 * details are collapsed to 3 lines and expand in place (two columns) only when the visitor asks for them.
 * `children` is unused now (kept so older call sites don't break); the popup renders plan.features itself.
 */
export function PlanHoverPopup({ plan, rect, options, months, onMonths, onChoose, onClose }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [plan?.id]);
  if (!plan || !rect || typeof window === "undefined") return null;
  const features = Array.isArray(plan.features) ? plan.features : [];
  const shown = open ? features : features.slice(0, 3);
  const hidden = Math.max(0, features.length - 3);
  const vw = window.innerWidth, vh = window.innerHeight;
  const width = Math.min(680, vw - 24);
  const left = Math.min(Math.max(12, rect.left + rect.width / 2 - width / 2), vw - width - 12);
  const estH = open ? 520 : 460;
  const top = Math.min(Math.max(12, rect.top + rect.height / 2 - estH / 2), Math.max(12, vh - estH - 12));
  return (
    <div className="fixed inset-0 z-[70]" data-testid="plan-hover-layer">
      <div className="navi-hover-bg absolute inset-0 bg-background/50 backdrop-blur-xl" onMouseEnter={onClose} onClick={onClose} />
      <div
        onMouseLeave={onClose}
        data-testid={`plan-hover-popup-${plan.id}`}
        style={{ left, top, width, maxHeight: vh - 24, transformOrigin: `${Math.round(rect.left + rect.width / 2 - left)}px ${Math.round(rect.top + rect.height / 2 - top)}px` }}
        className="navi-hover-pop absolute overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-b from-card to-background p-6 shadow-[0_40px_90px_-30px_rgba(0,0,0,.55)] ring-1 ring-black/5"
      >
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-lg font-semibold tracking-tight">{plan.name}</div>
            </div>
            <PlanTag plan={plan} />
          </div>
          <div className="mt-3"><PopupPrice plan={plan} /></div>

          <div className="mt-5 text-[0.6875rem] font-semibold uppercase tracking-[.16em] text-muted-foreground">Choose your duration</div>
          <div className="mt-2.5"><TermPicker options={options} value={months} onChange={onMonths} interval={plan.interval || "month"} compact /></div>

          {features.length > 0 && (
            <div className="mt-5 rounded-2xl border border-border/60 bg-background/60 p-3.5">
              <ul className={`gap-x-5 gap-y-1.5 ${open ? "grid sm:grid-cols-2" : "space-y-1.5"}`}>
                {shown.map((f, i) => (
                  <li key={i} className="flex gap-2 text-[0.8125rem] leading-snug"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /><span className="min-w-0">{f}</span></li>
                ))}
              </ul>
              {open && <div className="mt-3 border-t border-border/60 pt-3"><PopupHighlights plan={plan} options={options} /></div>}
              <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                {open ? "Show less" : hidden > 0 ? `View all details (+${hidden})` : "View details"}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
            </div>
          )}

          <button type="button" onClick={onChoose} className="mt-5 w-full min-h-12 rounded-full bg-foreground text-background text-sm font-semibold shadow-lg shadow-black/10 transition hover:opacity-90 active:scale-[.99]">
            Choose {plan.name}{months > 1 ? ` · ${months} months` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
